import "./ProductCommissionDetailsPage.scss";

import React, { useEffect, useMemo, useState } from "react";
import {
  faArrowLeft,
  faCalendarDays,
  faMoneyBillTransfer,
  faReceipt,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";

import AppLoading from "../../../commons/AppLoading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../commons/HallAppToast";
import MenuTitle from "../../../commons/menu/MenuTitle";
import ProductCommissionAPI from "../ProductCommissionAPI";

const productCommissionAPI = new ProductCommissionAPI();

function toMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("pt-BR");
}

function formatPaymentMethod(value) {
  const map = {
    PIX: "Pix",
    CASH: "Dinheiro",
    BANK_TRANSFER: "Transferência bancária",
    TRANSFER: "Transferência bancária",
    DEBIT_CARD: "Cartão de débito",
    CREDIT_CARD: "Cartão de crédito",
    OTHER: "Outro",
  };

  return map[value] || value || "-";
}

function formatStatus(value) {
  const map = {
    DONE: "Concluído",
    CANCELED: "Cancelado",
  };

  return map[value] || value || "-";
}

export default function ProductCommissionDetailsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const navigate = useNavigate();
  const { employeeId } = useParams();

  const loadPaymentHistory = async () => {
    try {
      setIsLoading(true);

      const response = await productCommissionAPI.findPaymentHistory(employeeId);
      setPaymentHistory(response?.data || response || []);
    } catch (e) {
      setPaymentHistory([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPayment = async (item) => {
    try {
      await productCommissionAPI.cancelPayment(item.paymentId);
  
      HallAppToast.success?.("Pagamento cancelado com sucesso.");
      await loadPaymentHistory();
    } catch (e) {
      HallAppToast.genericError();
    }
  };

  useEffect(() => {
    loadPaymentHistory();
  }, [employeeId]);

  const employeeName = useMemo(() => {
    if (!paymentHistory.length) {
      return "Profissional";
    }

    return paymentHistory[0]?.employeeName || "Profissional";
  }, [paymentHistory]);

  return (
    <div className="container product-commission-details-page">
      <div className="product-commission-details-page__back-wrapper">
        <button
          type="button"
          className="product-commission-details-page__back-btn"
          onClick={() => navigate("/produtos/comissoes")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Voltar</span>
        </button>
      </div>

      <MenuTitle
        title="Histórico de pagamentos"
        subtitle="Acompanhe os pagamentos de comissão já realizados para este profissional."
        showArrowInfo={false}
      />

      <section className="product-commission-details-page__employee-card">
        <div className="product-commission-details-page__employee-avatar">
          <FontAwesomeIcon icon={faUserTie} />
        </div>

        <div className="product-commission-details-page__employee-content">
          <h3>{employeeName}</h3>
          <p>Visualize os pagamentos já realizados e os detalhes de cada lançamento.</p>
        </div>
      </section>

      <section className="product-commission-details-page__history-section">
        <div className="product-commission-details-page__history-header">
          <div>
            <h3>Pagamentos realizados</h3>
            <p>
              Confira abaixo o histórico de pagamentos de comissão deste profissional.
            </p>
          </div>
        </div>

        {isLoading ? (
          <AppLoading />
        ) : paymentHistory.length === 0 ? (
          <div className="product-commission-details-page__empty">
            Nenhum pagamento encontrado para este profissional.
          </div>
        ) : (
          <div className="product-commission-details-page__history-list">
            {paymentHistory.map((item) => (
              <div
                className="product-commission-details-page__payment-card"
                key={item.paymentId}
              >
                <div className="product-commission-details-page__payment-top">
                  <div className="product-commission-details-page__payment-main">
                    <div className="product-commission-details-page__payment-icon">
                      <FontAwesomeIcon icon={faMoneyBillTransfer} />
                    </div>

                    <div>
                      <h4>{toMoney(item.paidAmount || item.totalAmount)}</h4>
                      <span>
                        {formatPaymentMethod(item.paymentMethod)} •{" "}
                        {formatStatus(item.status)}
                      </span>
                    </div>
                  </div>

                  <div className="product-commission-details-page__payment-badge">
                    #{item.paymentId}
                  </div>
                </div>

                <div className="product-commission-details-page__payment-info">
                  <div className="product-commission-details-page__info-row">
                    <span>
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Data do pagamento
                    </span>
                    <strong>{formatDateTime(item.paymentDate)}</strong>
                  </div>

                  <div className="product-commission-details-page__info-row">
                    <span>
                      <FontAwesomeIcon icon={faReceipt} />
                      Itens pagos
                    </span>
                    <strong>{item.itemsCount || 0}</strong>
                  </div>

                  <div className="product-commission-details-page__info-row">
                    <span>Total do pagamento</span>
                    <strong>{toMoney(item.totalAmount)}</strong>
                  </div>
                </div>

                {!!item.observation && (
                  <div className="product-commission-details-page__observation">
                    <span>Observação</span>
                    <p>{item.observation}</p>
                  </div>
                )}

                <div className="product-commission-details-page__payment-actions">
                  <button
                    type="button"
                    className="product-commission-details-page__cancel-btn"
                    onClick={() => handleCancelPayment(item)}
                    disabled={item.status === "CANCELED"}
                  >
                    Cancelar pagamento
                  </button>
                </div>
              </div>


            ))}
          </div>
        )}
      </section>
    </div>
  );
}